/**
 * Single source of truth for site-wide constants and content.
 * Content is from the client intake (docs/client-intake/*). The About page copy
 * is held verbatim in docs/source-material/about-us.txt and rendered by the About
 * page. Update this file to edit most of the site's words.
 *
 * NOTE: several values are placeholders pending client content — see
 * docs/client-intake/PENDING.md (testimonials, projects, real photos, GA/reCAPTCHA
 * keys, and the tagline are all provisional until confirmed).
 */

export const SITE = {
  name: "Kunda Electrical",
  legalName: "Kunda Electrical LLC",
  domain: "kundaelectricalllc.com",
  // Canonical serving host is www (the apex 301-redirects to it). Domain not yet
  // registered — see PENDING.md.
  url: "https://www.kundaelectricalllc.com",
  // Provisional tagline, grounded in the client's own About copy ("treated like
  // family"). Pending client sign-off (intake 05 open question).
  tagline: "Treating every customer like family.",
  description:
    "Kunda Electrical LLC is a licensed, insured electrical contractor serving homeowners and businesses across the Raleigh–Durham Triangle — panel upgrades, rewiring, EV chargers, lighting, generators, and electrical repair.",
  primaryCta: "Request an estimate",
} as const;

/** Contact details displayed across the site (phone-first; form is secondary). */
export const CONTACT = {
  phoneDisplay: "(984) 328-1444",
  phoneTel: "+19843281444",
  textable: true,
  email: "contact@kundaelectricalllc.com",
  // Monday–Friday, 8:00 AM – 5:00 PM (intake 01). After-hours available for a fee.
  hoursShort: "Mon–Fri, 8am–5pm",
  hoursNote: "After-hours service available for an additional fee.",
  responseTime: "We typically respond within 24 hours.",
  serviceCallFee: "$75",
  freeEstimates: "Free estimates on many jobs — ask when you call.",
} as const;

/** Service area — counties + most-served cities/towns (intake 01). */
export const SERVICE_AREA = {
  counties: ["Wake", "Durham", "Orange", "Johnston"],
  cities: [
    "Raleigh",
    "Cary",
    "Apex",
    "Durham",
    "Wake Forest",
    "Garner",
    "Chapel Hill",
    "Knightdale",
  ],
  note: "Serving the greater Raleigh–Durham Triangle. Outside our core area a travel fee ($25 minimum) may apply.",
} as const;

/** Trust signals shown on the site (licensing, experience, insurance). */
export const CREDENTIALS = {
  licenseNumber: "36808",
  licenseText: "NC Licensed Electrical Contractor (Lic. #36808)",
  yearsExperience: 16,
  founded: "January 2024",
  insured: "Licensed & fully insured",
  bbb: "Better Business Bureau member",
} as const;

export type NavItem = { label: string; href: string };

export const NAV: NavItem[] = [
  { label: "Services", href: "/services" },
  { label: "Projects", href: "/projects" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export type Highlight = { label: string; blurb: string };

/** Short highlight strip under the hero (replaces the template's capability strip). */
export const HIGHLIGHTS: Highlight[] = [
  { label: "Licensed & insured", blurb: "NC Electrical Contractor Lic. #36808" },
  { label: "16 years' experience", blurb: "Hands-on expertise on every job" },
  { label: "Locally owned", blurb: "Family-run, based in the Triangle" },
  { label: "Fast response", blurb: "We typically reply within 24 hours" },
];

export type Service = {
  id: string;
  title: string;
  summary: string;
  /** Jargon-free one-liner for homeowners who don't know electrical terms. */
  plain: string;
  /** Everyday terms & symptoms (for search and the contact wizard matching). */
  keywords: string[];
  outcomes: string[];
  /** Marks the services the client most wants to promote (intake Q34). */
  featured?: boolean;
};

/** Full service catalogue — anchor-linkable on /services#<id>. From intake 02. */
export const SERVICES: Service[] = [
  {
    id: "panel-upgrades",
    title: "Panel & Service Upgrades",
    plain: "Give your home more electrical capacity and a safer, modern breaker box.",
    keywords: [
      "panel", "panels", "electrical panel", "breaker panel", "breaker box", "breaker boxes",
      "fuse box", "fuse boxes", "fuses", "subpanel", "sub panel", "service upgrade",
      "service panel", "main panel", "load center", "100 amp", "150 amp", "200 amp", "400 amp",
      "amps", "amperage", "more power", "not enough power", "need more power", "add capacity",
      "upgrade panel", "replace panel", "panel replacement", "panel upgrade", "meter",
      "meter base", "service entrance", "grounding", "ground", "bonding", "surge protector",
      "surge protection", "whole home surge", "breaker", "breakers", "circuit breaker",
      "double tapped", "federal pacific", "zinsco", "panel full", "no room in panel", "heavy up",
    ],
    featured: true,
    summary:
      "Safer, higher-capacity electrical service for homes adding EV chargers, additions, or modern appliances — from breaker swaps to full panel replacements.",
    outcomes: [
      "Panel replacement or upgrade (100A to 200A)",
      "Subpanel installation for garages, additions, and shops",
      "Circuit breaker replacement and fuse box to breaker panel conversion",
      "Meter base repair or replacement",
      "Whole-home surge protection and grounding system upgrades",
    ],
  },
  {
    id: "wiring",
    title: "Wiring & Rewiring",
    plain: "Replace old or unsafe wiring, or wire a remodel, addition, or new space.",
    keywords: [
      "wire", "wires", "wiring", "rewire", "rewiring", "whole house rewire", "house rewiring",
      "old wiring", "outdated wiring", "cloth wiring", "knob and tube", "aluminum wiring",
      "copper wiring", "new wiring", "wire a room", "remodel", "remodel wiring", "renovation",
      "addition", "add a circuit", "new circuit", "dedicated circuit", "run wire", "run a line",
      "run power", "run electric", "extend wiring", "garage wiring", "basement wiring",
      "shed wiring", "outbuilding", "workshop", "new construction", "romex", "exposed wiring",
      "hot wire", "unsafe wiring", "wiring inspection", "burning smell", "sagging wire",
    ],
    featured: true,
    summary:
      "New wiring and safe replacement of old or hazardous wiring for remodels, additions, and whole-house projects.",
    outcomes: [
      "Whole-house rewiring and partial rewiring for remodels and additions",
      "Aluminum and knob-and-tube remediation",
      "New dedicated circuits for high-draw equipment",
      "Kitchen and bath remodel wiring",
      "Garage, shed, outbuilding, and new-construction wiring",
    ],
  },
  {
    id: "outlets-switches",
    title: "Outlets & Switches",
    plain: "Add, move, or replace outlets and switches — including USB and safety outlets.",
    keywords: [
      "outlet", "outlets", "plug", "plugs", "plug in", "receptacle", "receptacles",
      "wall outlet", "wall socket", "socket", "sockets", "add outlet", "more outlets",
      "move outlet", "new outlet", "replace outlet", "outlet not working", "dead outlet",
      "loose outlet", "switch", "switches", "light switch", "wall switch", "dimmer", "dimmers",
      "dimmer switch", "three way switch", "3 way switch", "four way switch", "smart switch",
      "smart outlet", "usb outlet", "gfci", "gfci outlet", "ground fault", "afci",
      "tamper resistant", "childproof outlet", "220v", "240v", "dryer outlet", "range outlet",
      "stove outlet", "welder outlet", "outdoor outlet", "floor outlet", "sparking outlet",
    ],
    featured: true,
    summary:
      "Add, replace, and upgrade outlets and switches — including safety-code devices and modern conveniences.",
    outcomes: [
      "Outlet installation or replacement, including USB outlets",
      "GFCI outlets and AFCI protection",
      "240V outlets for dryer, range, or welder",
      "Dimmers, smart switches, and three-way/four-way switch wiring",
      "Tamper-resistant outlets",
    ],
  },
  {
    id: "lighting-fans",
    title: "Lighting & Fans",
    plain: "Install or replace lights and ceiling fans — from recessed cans to chandeliers.",
    keywords: [
      "light", "lights", "lighting", "lightbulb", "light bulb", "light fixture", "fixture",
      "fixtures", "install light", "replace light", "new light", "hang a light", "ceiling light",
      "recessed light", "recessed lighting", "can light", "can lights", "pot lights", "downlight",
      "track light", "track lighting", "under cabinet", "puck lights", "chandelier", "chandeliers",
      "pendant", "pendant light", "pendants", "sconce", "wall sconce", "flush mount",
      "vanity light", "bathroom light", "closet light", "led", "led lights", "led retrofit",
      "dimmable", "ceiling fan", "ceiling fans", "fan", "fans", "exhaust fan", "bathroom fan",
      "vent fan", "attic fan", "replace fan", "wobbling fan", "smart light",
    ],
    summary:
      "Interior lighting and ceiling fans installed cleanly and safely — from recessed cans to chandeliers.",
    outcomes: [
      "Fixture installation or replacement",
      "Recessed lighting, track lighting, and under-cabinet lighting",
      "Chandelier and pendant installation",
      "LED retrofits",
      "Ceiling fan and bathroom/attic exhaust fan installation",
    ],
  },
  {
    id: "outdoor",
    title: "Outdoor & Exterior",
    plain: "Lighting, power, and wiring built for outside — decks, pools, and security lights.",
    keywords: [
      "outdoor", "outdoors", "outside", "exterior", "outdoor light", "exterior lighting",
      "landscape lighting", "landscape lights", "yard lights", "path lights", "pathway lighting",
      "garden lights", "security light", "security lighting", "flood light", "floodlight",
      "motion light", "motion sensor", "motion detector", "dusk to dawn", "porch light",
      "soffit lighting", "outdoor outlet", "exterior outlet", "weatherproof outlet", "deck",
      "deck lighting", "deck wiring", "patio", "patio lights", "pergola", "gazebo", "pool",
      "pool wiring", "pool pump", "spa", "hot tub", "holiday lights", "christmas lights",
      "string lights", "outdoor kitchen", "fountain", "pond pump", "gate opener",
    ],
    summary:
      "Lighting, power, and specialty wiring that stands up to the weather and makes the most of your outdoor space.",
    outcomes: [
      "Landscape, security, flood, and motion-sensor lighting",
      "Outdoor outlets and weatherproof covers",
      "Deck, patio, and pergola wiring",
      "Pool, spa, and hot tub wiring",
      "Holiday lighting installation",
    ],
  },
  {
    id: "ev-smart-home",
    title: "EV Charging & Smart Home",
    plain: "Charge your EV at home and set up smart thermostats, doorbells, and devices.",
    keywords: [
      "ev", "ev charger", "ev charging", "electric vehicle", "electric car", "car charger",
      "charge my car", "charging station", "home charger", "level 2", "level 2 charger",
      "level two", "240v charger", "nema 14-50", "14-50", "tesla", "tesla charger",
      "wall connector", "chargepoint", "juicebox", "emporia", "rivian", "hardwired charger",
      "garage charger", "smart home", "smart devices", "smart thermostat", "nest", "ecobee",
      "thermostat", "thermostat wiring", "smart doorbell", "video doorbell", "ring doorbell",
      "doorbell camera", "smart plug", "smart lock", "home automation", "alexa", "google home",
      "energy monitor", "energy monitoring",
    ],
    featured: true,
    summary:
      "Charge your EV at home and connect the smart devices you rely on — installed by a licensed electrician.",
    outcomes: [
      "Level 2 EV charger installation",
      "Smart thermostat wiring",
      "Video doorbell installation and smart home device setup",
      "Whole-home energy monitoring",
    ],
  },
  {
    id: "generators",
    title: "Generators & Backup Power",
    plain: "Keep the lights on during outages with a generator or battery backup.",
    keywords: [
      "generator", "generators", "backup power", "backup generator", "standby generator",
      "whole house generator", "whole home generator", "home generator", "natural gas generator",
      "propane generator", "generac", "kohler", "portable generator", "generator hookup",
      "generator install", "transfer switch", "automatic transfer switch", "manual transfer switch",
      "interlock", "interlock kit", "generator inlet", "power inlet", "battery backup",
      "home battery", "powerwall", "tesla powerwall", "solar battery", "backup battery",
      "power outage", "outages", "keep power on", "lose power", "no power", "storm power",
      "emergency power",
    ],
    summary:
      "Keep the lights on during outages with the right backup power solution for your home.",
    outcomes: [
      "Standby generator installation",
      "Transfer switch installation",
      "Portable generator inlet and interlock",
      "Home battery backup",
    ],
  },
  {
    id: "safety-repair",
    title: "Safety, Repair & Inspection",
    plain: "Fix flickering lights, dead outlets, tripping breakers, and other problems — safely.",
    keywords: [
      "repair", "repairs", "fix", "electrical repair", "troubleshoot", "troubleshooting",
      "diagnose", "not working", "no power", "power out", "lost power", "power went out",
      "partial power", "flicker", "flickering", "flickering lights", "lights flicker",
      "dimming lights", "buzzing", "humming", "buzzing outlet", "dead outlet",
      "outlet not working", "tripping breaker", "breaker keeps tripping", "breaker trips",
      "keeps tripping", "tripped breaker", "reset breaker", "breaker not resetting", "sparks",
      "sparking", "arcing", "burning smell", "smells like burning", "hot outlet", "warm switch",
      "shock", "getting shocked", "smoke detector", "smoke alarm", "carbon monoxide detector",
      "co detector", "hardwired smoke", "chirping smoke detector", "code violation",
      "code correction", "failed inspection", "electrical inspection", "safety inspection",
      "emergency", "emergency electrician", "urgent", "after hours",
    ],
    summary:
      "Troubleshooting and repairs that get to the root cause, plus inspections and code corrections you can rely on.",
    outcomes: [
      "Troubleshooting flickering lights, dead outlets, and tripping breakers",
      "Emergency repairs (after-hours available for a fee)",
      "Electrical safety inspections and home-sale repair lists",
      "Code compliance corrections",
      "Hardwired smoke and CO detectors; permits and inspection coordination",
    ],
  },
  {
    id: "appliance-hookups",
    title: "Appliance & Equipment Hookups",
    plain: "Wire and connect the appliances and equipment that keep your home running.",
    keywords: [
      "appliance", "appliances", "appliance hookup", "appliance install", "hook up", "hookup",
      "connect appliance", "wire appliance", "range", "electric range", "stove", "electric stove",
      "cooktop", "oven", "wall oven", "double oven", "dryer", "electric dryer", "dryer hookup",
      "dryer circuit", "washer", "dishwasher", "garbage disposal", "disposal", "microwave",
      "range hood", "water heater", "electric water heater", "tankless water heater", "tankless",
      "hot water heater", "hvac", "air conditioner", "ac unit", "furnace", "heat pump",
      "mini split", "ductless", "well pump", "sump pump", "ejector pump", "hot tub hookup",
      "freezer circuit", "garage fridge",
    ],
    summary:
      "Proper circuits and connections for the appliances and equipment that make a home run.",
    outcomes: [
      "Appliance circuits for range, oven, and dryer",
      "HVAC and mini-split connections",
      "Water heater hookup, including tankless",
      "Well pump and sump pump wiring",
    ],
  },
  {
    id: "low-voltage",
    title: "Low-Voltage & Data",
    plain: "Neat, hidden wiring for TV, internet, cameras, and doorbells.",
    keywords: [
      "low voltage", "data", "data wiring", "network", "network cable", "networking", "ethernet",
      "ethernet cable", "cat5", "cat5e", "cat6", "internet wiring", "internet drop", "wifi",
      "wireless access point", "router wiring", "tv", "tv mount", "mount tv", "tv wall mount",
      "hide tv wires", "conceal wires", "hdmi", "coax", "coaxial", "cable outlet", "antenna",
      "security camera", "security cameras", "cctv", "camera wiring", "surveillance",
      "doorbell wire", "doorbell wiring", "intercom", "speaker wire", "in wall speakers",
      "home theater", "structured wiring", "phone jack", "thermostat wire",
    ],
    summary:
      "Clean, concealed wiring for the connected parts of your home — data, TV, cameras, and doorbells.",
    outcomes: [
      "Ethernet and network cabling",
      "TV mounting with concealed wiring",
      "Security camera wiring",
      "Doorbell and intercom wiring",
    ],
  },
];

/**
 * Plain-language groupings of the service catalogue. These are the "what do you
 * need?" buckets a homeowner recognizes — used by the Services page filters and
 * the Contact wizard's first step. Every service belongs to exactly one group.
 */
export type ServiceGroup = {
  id: string;
  label: string;
  /** Plain-language description of the everyday situations this group covers. */
  blurb: string;
  serviceIds: string[];
};

export const SERVICE_GROUPS: ServiceGroup[] = [
  {
    id: "repairs",
    label: "Repairs & safety",
    blurb: "Something isn't working, keeps tripping, or feels unsafe.",
    serviceIds: ["safety-repair"],
  },
  {
    id: "power",
    label: "Panels & power",
    blurb: "More capacity, new wiring, or backup power for outages.",
    serviceIds: ["panel-upgrades", "wiring", "generators"],
  },
  {
    id: "lighting",
    label: "Lighting & fans",
    blurb: "Install or replace lights, ceiling fans, and outdoor lighting.",
    serviceIds: ["lighting-fans", "outdoor"],
  },
  {
    id: "outlets",
    label: "Outlets & appliances",
    blurb: "Add outlets and switches, or hook up appliances and equipment.",
    serviceIds: ["outlets-switches", "appliance-hookups"],
  },
  {
    id: "ev-smart",
    label: "EV, smart home & data",
    blurb: "EV charging, smart devices, TV, internet, and cameras.",
    serviceIds: ["ev-smart-home", "low-voltage"],
  },
];

/** Lookup a service by id. */
export function serviceById(id: string): Service | undefined {
  return SERVICES.find((s) => s.id === id);
}

/** The group a service belongs to (used to pre-seed the wizard from a deep link). */
export function groupOfService(id: string): ServiceGroup | undefined {
  return SERVICE_GROUPS.find((g) => g.serviceIds.includes(id));
}

export type Step = { title: string; body: string };

/** "How it works" — the numbered spine on the home page. */
export const HOW_WE_WORK: Step[] = [
  {
    title: "Reach out",
    body: "Call or text (984) 328-1444, or send a quick note through the site. Tell us what's going on.",
  },
  {
    title: "Estimate or diagnosis",
    body: "We give free estimates on many jobs; for troubleshooting there's a $75 diagnostic visit so you get a real answer, not a guess.",
  },
  {
    title: "Licensed work, done right",
    body: "A licensed electrician does the work to code, pulls permits where needed, and leaves the site clean.",
  },
  {
    title: "Stand behind it",
    body: "We treat every customer like family — if something isn't right, we make it right.",
  },
];

export type Differentiator = { title: string; body: string };

export const DIFFERENTIATORS: Differentiator[] = [
  {
    title: "Licensed & insured",
    body: "NC Licensed Electrical Contractor (Lic. #36808), fully insured — so the work is done to code and you're covered.",
  },
  {
    title: "16 years of experience",
    body: "Kunda is young by the calendar, but every panel and every wire is backed by sixteen years of hands-on electrical work.",
  },
  {
    title: "Locally owned and family-run",
    body: "\"Kunda\" means family. We're based in the Triangle and treat every customer — and every home — like our own.",
  },
  {
    title: "Straight answers, fair pricing",
    body: "Free estimates on many jobs, clear diagnostics, and honest recommendations. No upselling you don't need.",
  },
  {
    title: "Fast to respond",
    body: "We typically reply within 24 hours, with after-hours service available when you can't wait.",
  },
  {
    title: "Residential & commercial",
    body: "No job is too small and no building is too big — from a family kitchen to a commercial storefront.",
  },
];

export type Faq = { q: string; a: string };

/** Drafted from the intake brief — pending client review (intake 02). */
export const FAQS: Faq[] = [
  {
    q: "What areas do you serve?",
    a: "We serve the greater Raleigh–Durham Triangle, including Raleigh, Cary, Apex, Durham, Wake Forest, Garner, Chapel Hill, and Knightdale — across Wake, Durham, Orange, and Johnston counties. Outside our core area a travel fee ($25 minimum) may apply.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes. Kunda Electrical is a licensed North Carolina electrical contractor (Lic. #36808) and is fully insured.",
  },
  {
    q: "Do you offer free estimates?",
    a: "We offer free estimates on many jobs — just ask when you call. For troubleshooting and diagnostics there is a $75 service visit, so we can find the real cause of the problem.",
  },
  {
    q: "Do you handle emergencies or after-hours work?",
    a: "Yes. After-hours service is available for an additional fee — call or text (984) 328-1444 and we'll let you know what we can do.",
  },
  {
    q: "How do I get started?",
    a: "Call or text (984) 328-1444, or send a note through our contact form. We typically respond within 24 hours.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept cash, check, credit and debit cards, Zelle, and Venmo.",
  },
];

/** Payment methods (intake Q38). */
export const PAYMENTS: string[] = ["Cash", "Check", "Credit / debit card", "Zelle", "Venmo"];

export type Testimonial = { quote: string; name: string };

/**
 * PLACEHOLDER — the client will send 2–3 real testimonials (with first names).
 * Permission to display reviews is granted. Empty until received; the Home page
 * shows a tasteful placeholder while this is empty. See PENDING.md.
 */
export const TESTIMONIALS: Testimonial[] = [];

export type Project = {
  id: string;
  title: string;
  category: string;
  location?: string;
  description: string;
  /** Image srcs (in /public), portrait orientation. */
  images: { src: string; alt: string }[];
};

/**
 * Real Kunda Electrical project photos and copy, organized by the type of work
 * (client-provided; see docs/client-intake). No specific job addresses were
 * supplied, so `location` is omitted rather than invented.
 */
export const PROJECTS: Project[] = [
  {
    id: "ev-charging",
    title: "EV Charging Installations",
    category: "EV Charging & Smart Home",
    description:
      "From residential wall-mounted chargers to commercial fleet charging stations, these projects show practical EV charging solutions built around the needs of each property. The installations include indoor and weather-exposed charging equipment, panel integration, secure mounting, organized cable management, and dependable electrical connections—creating convenient systems that are ready for everyday charging.",
    images: [
      {
        src: "/projects/ev-charging/1.jpg",
        alt: "Open commercial EV charging cabinet with organized control boards and wiring, installed next to a school bus",
      },
      {
        src: "/projects/ev-charging/2.jpg",
        alt: "Tesla wall connector mounted in a residential garage with a neatly coiled charging cable",
      },
      {
        src: "/projects/ev-charging/3.jpg",
        alt: "Pedestal-mounted commercial EV fleet charging station installed beside a parked school bus",
      },
      {
        src: "/projects/ev-charging/4.jpg",
        alt: "Tesla Wall Connector with a neatly coiled cable mounted beside an open, labeled garage electrical panel",
      },
      {
        src: "/projects/ev-charging/5.jpg",
        alt: "Wall-mounted Tesla Wall Connector with a coiled cable next to a residential garage panel",
      },
      {
        src: "/projects/ev-charging/6.jpg",
        alt: "Black EVIQO EV charger with a holstered connector and coiled cable mounted on light exterior siding",
      },
      {
        src: "/projects/ev-charging/7.jpg",
        alt: "EVIQO EV charger installed on an exterior wall beside a door, overlooking a fenced backyard",
      },
    ],
  },
  {
    id: "panel-service-upgrades",
    title: "Electrical Panel & Service Upgrades",
    category: "Panel & Service Upgrades",
    description:
      "These electrical panel and service projects highlight the essential infrastructure that keeps homes and commercial properties powered safely and reliably. The work includes main panels and subpanels, service equipment, meter configurations, disconnects, circuit labeling, and carefully routed conduit—each completed with close attention to accessibility, organization, and the electrical demands of the property.",
    images: [
      {
        src: "/projects/panel-service-upgrades/1.jpg",
        alt: "Garage electrical panel with labeled breakers and new conduit run to an exterior disconnect",
      },
      {
        src: "/projects/panel-service-upgrades/2.jpg",
        alt: "Dual meter and emergency disconnect enclosures mounted on a brick exterior wall",
      },
      {
        src: "/projects/panel-service-upgrades/3.jpg",
        alt: "Exterior disconnect box mounted on home siding near a deck",
      },
      {
        src: "/projects/panel-service-upgrades/6.jpg",
        alt: "House and garage meter and disconnect enclosures mounted on a brick exterior wall",
      },
      {
        src: "/projects/panel-service-upgrades/8.jpg",
        alt: "Wide view of an open panel installation with main breaker and organized wiring",
      },
      {
        src: "/projects/panel-service-upgrades/9.jpg",
        alt: "Meter and service disconnect enclosures mounted beneath a roofline on a brick exterior wall",
      },
      {
        src: "/projects/panel-service-upgrades/10.jpg",
        alt: "Open Siemens main panel with hand-labeled circuits and yellow branch wiring feeding in from above",
      },
    ],
  },
  {
    id: "lighting-installations",
    title: "Lighting Installations",
    category: "Lighting & Fans",
    description:
      "Thoughtfully installed lighting can improve comfort, visibility, security, and the overall character of a space. These projects range from recessed lighting and decorative ceiling fixtures to exterior security lights and high-output custom garage systems, demonstrating solutions tailored to everyday function, renovation goals, and the look of the finished environment.",
    images: [
      {
        src: "/projects/lighting-installations/1.jpg",
        alt: "Modern brass linear pendant light fixture hanging above a dining table",
      },
      {
        src: "/projects/lighting-installations/2.jpg",
        alt: "Exterior motion-sensor security light mounted under a roof eave",
      },
      {
        src: "/projects/lighting-installations/3.jpg",
        alt: "Custom honeycomb LED lighting array covering the ceiling of a garage workshop",
      },
      {
        src: "/projects/lighting-installations/4.jpg",
        alt: "Newly installed recessed can lights in a smooth ceiling during a room renovation",
      },
      {
        src: "/projects/lighting-installations/5.jpg",
        alt: "Decorative crystal drum flush-mount ceiling fixture with hanging prisms in a sage-green room",
      },
      {
        src: "/projects/lighting-installations/6.jpg",
        alt: "Modern ring-style LED flush-mount light with an enclosed fan, flanked by recessed downlights",
      },
    ],
  },
];
